// CaseInformation.jsx
// Display case information in a table format.

// Copyright HS Analysis GmbH, 2024
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Icon imports
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

// File imports
import ImageIcon from "@mui/icons-material/Image";

//HSA Imports
import Backend from "../../common/utils/Backend";
import { CaseContext } from "./CaseTableDockSideBar";
import { CaseContext as FileCaseContext } from "../../fileViewer/components/FileViewerDockSideBar";

const CaseInformation = () => {
  const CC = React.useContext(CaseContext) || React.useContext(FileCaseContext);
  const { selCase } = CC;
  const classes = useStyles();
  const [caseTableRows, setCaseTableRows] = React.useState([]);
  const [caseColumns, setCaseColumns] = React.useState([]);
  const [attachments, setAttachments] = React.useState([]);
  const [reportPdf, setReportPdf] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;
    if (!selCase) return;

    Backend.getCaseColumns().then((caseColumnsResponse) => {
      if (!isMounted) return;
      setCaseColumns(caseColumnsResponse);
    });

    Backend.getCaseAttachments(selCase.Id).then((response) => {
      if (!isMounted || !response?.attachments) return;
      setAttachments(response.attachments);
      const pdf = response.attachments.find(
        (f) => (f.split(".").pop() || "").toLowerCase() === "pdf"
      );
      setReportPdf(pdf || null);
    });

    return () => {
      isMounted = false;
    };
  }, [selCase]);

  React.useEffect(() => {
    if (!selCase) return;
    if (!caseColumns) return;
    if (caseTableRows.length > 0) return;

    const tableRows = [];
    let colIdx = 0;
    let row = [];
    let labelLength = 0;
    let valueLength = 0;
    for (const caseCol of caseColumns) {
      const info = {
        label: caseCol.label ? caseCol.label : "",
        value: selCase[caseCol.name] ? selCase[caseCol.name] : "",
      };
      row.push(info);
      labelLength += info.label.length;
      valueLength += info.value.length;
      colIdx++;
      // max 1 column right now. For more columns, change colIdx === 1 to colIdx === n and the desired label or value length
      if (colIdx === 1 || labelLength > 200 || valueLength > 200) {
        tableRows.push(row);
        row = [];
        colIdx = 0;
      }
    }
    setCaseTableRows(tableRows);
  }, [selCase, caseColumns]);

  const getDynamicWidthStyle = (length) => {
    length = length > 0 ? length : 1;
    return {
      width: `${100 / length}%`,
    };
  };

  if (!selCase) {
    return <div className={classes.root}></div>;
  }

  return (
    <div className={classes.root}>
      <div>
        {caseTableRows.map((caseTableRow, rowIdx) => (
          <TableContainer key={rowIdx} component={Paper}>
            <Table>
              <TableHead className={classes.head}>
                <TableRow>
                  {caseTableRow.map((info, idx) => (
                    <TableCell
                      key={idx}
                      className={classes.headCell}
                      style={getDynamicWidthStyle(caseTableRow.length)}
                    >
                      {info.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {caseTableRow.map((info, idx) => (
                    <TableCell key={idx} className={classes.bodyCell}>
                      {info.value}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ))}
        {reportPdf && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead className={classes.head}>
                <TableRow>
                  <TableCell className={classes.headCell}>Report</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.bodyCell}>
                    <div className={classes.attachment}>
                      <PictureAsPdfIcon className={classes.attachmentIcon} />
                      <a
                        href={Backend.viewCaseAttachment(selCase.Id, reportPdf)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {reportPdf}
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead className={classes.head}>
              <TableRow>
                <TableCell className={classes.headCell}>Anhänge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.bodyCell}>
                  {attachments
                    .filter((attachment) => attachment !== reportPdf)
                    .map((attachment, idx) => {
                      const fileEnding = (
                        attachment.split(".").pop() || ""
                      ).toLowerCase();
                      const isImage = [
                        "png",
                        "jpg",
                        "jpeg",
                        "gif",
                        "webp",
                        "bmp",
                        "tif",
                        "tiff",
                      ].includes(fileEnding);
                      const isPdf = fileEnding === "pdf";
                      const icon = isPdf ? (
                        <PictureAsPdfIcon className={classes.attachmentIcon} />
                      ) : isImage ? (
                        <ImageIcon className={classes.attachmentIcon} />
                      ) : (
                        <InsertDriveFileIcon
                          className={classes.attachmentIcon}
                        />
                      );
                      const url = isPdf
                        ? Backend.viewCaseAttachment(selCase.Id, attachment)
                        : Backend.downloadCaseAttachment(
                            selCase.Id,
                            attachment
                          );
                      const imageViewerHref = isImage
                        ? `/image_view?caseId=${encodeURIComponent(
                            selCase.Id
                          )}&fileName=${encodeURIComponent(attachment)}`
                        : null;
                      return (
                        <div key={idx} className={classes.attachment}>
                          {icon}
                          {isPdf ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {attachment}
                            </a>
                          ) : isImage ? (
                            <a
                              href={imageViewerHref}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {attachment}
                            </a>
                          ) : (
                            <a href={url} download>
                              {attachment}
                            </a>
                          )}
                          {isImage && (
                            <a
                              href={imageViewerHref}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={Backend.viewCaseAttachment(
                                  selCase.Id,
                                  attachment
                                )}
                                alt={attachment}
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  marginLeft: "10px",
                                }}
                              />
                            </a>
                          )}
                        </div>
                      );
                    })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

CaseInformation.propTypes = {
  selCase: PropTypes.object,
};

export default CaseInformation;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    overflow: "auto",
  },
  head: {
    background: "rgba(0, 0, 0, 0.125)",
  },
  headCell: {
    fontWeight: "bold",
    fontSize: "15px",
  },
  attachmentIcon: {
    verticalAlign: "middle",
    color: theme.palette.secondary.main,
  },
  attachment: {
    display: "inline-block",
    marginRight: "20px",
  },
}));
